import type { FormEvent } from 'react'

import type { SearchBy, SearchCriteria, SuggestField } from '../api/types'
import { MAX_LENGTHS } from '../lib/validation'
import type { ValidationErrors } from '../lib/validation'
import { ComboField } from './ComboField'
import { Button, Callout, Checkbox, SelectField, TextField } from './ui'

interface SearchCardProps {
  criteria: SearchCriteria
  isSearching: boolean
  /** Per field messages from the last attempted search. Empty until one fails. */
  errors: ValidationErrors
  /** Incremented on every submit, so the suggestion lists shut. */
  closeToken: number
  /** From spGetLocalityStates, or the fallback list if it could not be read. */
  stateOptions: readonly string[]
  onChange: <K extends keyof SearchCriteria>(field: K, value: SearchCriteria[K]) => void
  onSearchByChange: (searchBy: SearchBy) => void
  onSearch: () => void
  onClear: () => void
  /** Values to offer under one address field. See ComboField. */
  onSuggest: (field: SuggestField, term: string, signal: AbortSignal) => Promise<string[]>
}

const SEARCH_BY_OPTIONS: Array<{ value: SearchBy; label: string }> = [
  { value: 'address', label: 'Address' },
  { value: 'property', label: 'Property' },
]

export function SearchCard({
  criteria,
  isSearching,
  errors,
  closeToken,
  stateOptions,
  onChange,
  onSearchByChange,
  onSearch,
  onClear,
  onSuggest,
}: SearchCardProps) {
  // Enter in any field submits, matching the legacy form.
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSearch()
  }

  // Every criterion except the field's own narrows its suggestions, so the list
  // is refreshed when one of the others changes.
  function contextKey(field: SuggestField) {
    return [
      criteria.state,
      field === 'postcode' ? '' : criteria.postcode.trim(),
      field === 'suburb' ? '' : criteria.suburb.trim(),
      field === 'streetName' ? '' : criteria.streetName.trim(),
    ].join('|')
  }

  return (
    <section className="card card--accent" aria-label="Search criteria">
      <form className="card__body" onSubmit={handleSubmit} noValidate>
        <fieldset className="fieldset">
          <legend className="fieldset__legend">Search by</legend>
          <div className="segmented">
            {SEARCH_BY_OPTIONS.map((option) => (
              <span className="segmented__option" key={option.value}>
                <input
                  className="segmented__input"
                  type="radio"
                  name="searchBy"
                  id={`searchBy-${option.value}`}
                  value={option.value}
                  checked={criteria.searchBy === option.value}
                  onChange={() => onSearchByChange(option.value)}
                />
                <label className="segmented__label" htmlFor={`searchBy-${option.value}`}>
                  {option.label}
                </label>
              </span>
            ))}
          </div>
        </fieldset>

        <div className="search-layout">
          <div>
            {criteria.searchBy === 'address' ? (
              <div className="field-grid">
                <SelectField
                  label="State"
                  value={criteria.state}
                  options={stateOptions}
                  onValueChange={(value) => onChange('state', value)}
                />
                <ComboField
                  label="Postcode"
                  value={criteria.postcode}
                  inputMode="numeric"
                  maxLength={4}
                  error={errors.postcode}
                  closeToken={closeToken}
                  onValueChange={(value) => onChange('postcode', value)}
                  onSuggest={(term, signal) => onSuggest('postcode', term, signal)}
                  contextKey={contextKey('postcode')}
                />
                <ComboField
                  label="Suburb"
                  value={criteria.suburb}
                  maxLength={MAX_LENGTHS.suburb}
                  error={errors.suburb}
                  closeToken={closeToken}
                  onValueChange={(value) => onChange('suburb', value)}
                  onSuggest={(term, signal) => onSuggest('suburb', term, signal)}
                  contextKey={contextKey('suburb')}
                />
                <ComboField
                  label="Street Name"
                  value={criteria.streetName}
                  maxLength={MAX_LENGTHS.streetName}
                  error={errors.streetName}
                  closeToken={closeToken}
                  onValueChange={(value) => onChange('streetName', value)}
                  onSuggest={(term, signal) => onSuggest('streetName', term, signal)}
                  contextKey={contextKey('streetName')}
                />
                <TextField
                  label="Street Number"
                  value={criteria.streetNumber}
                  inputMode="numeric"
                  maxLength={MAX_LENGTHS.streetNumber}
                  error={errors.streetNumber}
                  onValueChange={(value) => onChange('streetNumber', value)}
                />
                <TextField
                  label="Unit No"
                  value={criteria.unitNumber}
                  inputMode="numeric"
                  maxLength={MAX_LENGTHS.unitNumber}
                  error={errors.unitNumber}
                  onValueChange={(value) => onChange('unitNumber', value)}
                />
              </div>
            ) : (
              <div className="field-grid">
                <TextField
                  label="Property Key"
                  value={criteria.propertyKey}
                  inputMode="numeric"
                  maxLength={MAX_LENGTHS.propertyKey}
                  error={errors.propertyKey}
                  onValueChange={(value) => onChange('propertyKey', value)}
                />
                <TextField
                  label="Strata Plan Number"
                  value={criteria.strataPlanNumber}
                  maxLength={MAX_LENGTHS.strataPlanNumber}
                  error={errors.strataPlanNumber}
                  onValueChange={(value) => onChange('strataPlanNumber', value)}
                />
              </div>
            )}

            {/* The search as a whole, rather than one field: too broad to run,
                or nothing entered at all. Shown here because no single field is
                at fault. */}
            {errors.form ? (
              <div className="search-form-error">
                <Callout>{errors.form}</Callout>
              </div>
            ) : null}

            <div className="button-row">
              <Button type="submit" disabled={isSearching}>
                {isSearching ? 'Searching' : 'Search'}
              </Button>
              <Button variant="secondary" onClick={onClear} disabled={isSearching}>
                Clear Values
              </Button>
            </div>
          </div>

          <fieldset className="fieldset">
            <legend className="fieldset__legend">Search options</legend>
            <div className="option-list">
              <Checkbox
                label="Only Display Most Reliable Score"
                checked={criteria.mostReliableOnly}
                onCheckedChange={(checked) => onChange('mostReliableOnly', checked)}
              />
              <Checkbox
                label="Include Units"
                checked={criteria.includeUnits}
                onCheckedChange={(checked) => onChange('includeUnits', checked)}
              />
              <Checkbox
                label="Include Bushfire Peril Data"
                checked={criteria.includeBushfire}
                onCheckedChange={(checked) => onChange('includeBushfire', checked)}
              />
            </div>
          </fieldset>
        </div>
      </form>
    </section>
  )
}
